/**
 * ag-cart.js — cart drawer behaviour (B10)
 *
 * Two things Horizon does not do on its own:
 *
 * 1. Checkout loading feedback. The checkout button is a plain native POST.
 *    On a slow connection nothing visibly happens for seconds, so people click
 *    two to four times and every click restarts the navigation.
 *
 * 2. Stale-drawer guard. Coming back from the checkout is a `back_forward`
 *    navigation served from the plain HTTP cache (`persisted: false`). The
 *    server-rendered drawer snapshot is then whatever it was when the page was
 *    left — often "your cart is empty" while the cart is full. The badge is
 *    correct, the drawer is not.
 *
 * Both listeners live on the drawer HOST (`<theme-drawer id="cart-drawer">`,
 * which carries `data-skip-node-update`). The form and the button inside it are
 * rebuilt on every cart mutation; the host survives that.
 */

import { sectionRenderer } from '@theme/section-renderer';

const HOST_ID = 'cart-drawer';
const SECTION_ID = 'cart-drawer-section';
const BUSY_FALLBACK_MS = 15000;

/** @type {number | undefined} */
let busyTimer;

/** @returns {HTMLElement | null} */
function host() {
  return document.getElementById(HOST_ID);
}

/** @returns {HTMLButtonElement | null} */
function checkoutButton() {
  return /** @type {HTMLButtonElement | null} */ (host()?.querySelector('button[name="checkout"]') ?? null);
}

function setBusy() {
  const button = checkoutButton();
  if (!button) return;

  button.setAttribute('aria-busy', 'true');
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  clearTimeout(busyTimer);
  busyTimer = window.setTimeout(clearBusy, BUSY_FALLBACK_MS);
}

function clearBusy() {
  clearTimeout(busyTimer);
  busyTimer = undefined;

  const button = checkoutButton();
  if (!button?.hasAttribute('aria-busy')) return;

  button.removeAttribute('aria-busy');
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
}

/**
 * Blocks a second click while the first checkout navigation is still running.
 * Capture phase so it runs before the button's own default action.
 * @param {MouseEvent} event
 */
function onClick(event) {
  const target = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
  if (!target) return;

  if (target.getAttribute('aria-busy') === 'true') {
    event.preventDefault();
    event.stopPropagation();
  }
}

/**
 * @param {SubmitEvent} event
 */
function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  // Quick-buy / product forms inside the drawer are handled by the product
  // form logic — never put them in the checkout busy state.
  if (form.dataset.type === 'add-to-cart-form' || form.id !== 'cart-form') return;

  const submitter = /** @type {HTMLElement | null} */ (event.submitter);
  if (submitter && submitter.getAttribute('name') !== 'checkout') return;

  if (checkoutButton()?.getAttribute('aria-busy') === 'true') {
    event.preventDefault();
    return;
  }

  setBusy();
}

/** Item count as it currently stands in the rendered drawer, or null if unknown. */
function renderedItemCount() {
  const drawer = host();
  if (!drawer) return null;

  const counter = drawer.querySelector('[data-ag-cart-count]');
  if (counter) {
    const value = Number(counter.getAttribute('data-ag-cart-count'));
    return Number.isFinite(value) ? value : null;
  }

  // No counter rendered means the empty state is on screen.
  if (drawer.querySelector('.cart-drawer--empty, [data-cart-drawer-empty]')) return 0;

  return null;
}

function refresh() {
  return sectionRenderer
    .renderSection(SECTION_ID, { cache: false, mode: 'hydration' })
    .catch(() => {
      /* A failed refresh must never break the drawer — stay with what is on screen. */
    });
}

/** Compares the rendered count with the real cart and re-renders on a mismatch. */
async function verifyAgainstCart() {
  const rendered = renderedItemCount();
  if (rendered == null) return;

  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return;

    const cart = await response.json();
    if (typeof cart?.item_count === 'number' && cart.item_count !== rendered) {
      await refresh();
    }
  } catch {
    /* Offline, aborted, throttled — silently keep the current render. */
  }
}

/**
 * @param {PageTransitionEvent} event
 */
function onPageShow(event) {
  clearBusy();

  // A persisted (bfcache) restore is already handled by the theme itself.
  if (event.persisted) return;

  const entry = /** @type {PerformanceNavigationTiming | undefined} */ (
    performance.getEntriesByType('navigation')[0]
  );
  const isBackForward =
    entry?.type === 'back_forward' ||
    // Older engines without the Navigation Timing Level 2 entry.
    (typeof performance.navigation !== 'undefined' && performance.navigation.type === 2);

  if (isBackForward) refresh();
}

function init() {
  const drawer = host();
  if (!drawer || drawer.dataset.agCartBound === 'true') return;
  drawer.dataset.agCartBound = 'true';

  drawer.addEventListener('click', onClick, true);
  drawer.addEventListener('submit', onSubmit, true);
  drawer.addEventListener('theme-drawer:open', verifyAgainstCart);

  window.addEventListener('pageshow', onPageShow);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
